import {
  getDefaultFiltersForKey,
  LEGACY_TOP_LEVEL_FILTER_KEYS,
} from "@components/constants/filterDefaultsRegistry";
import { LD_FLAG_IDEA_MDP_526 } from "@components/constants/launchDarklyFlagKeys";

/**
 * Sync cache for idea-MDP-526. Set from featureFlags store at boot so
 * getFilters/updateFilters stay synchronous.
 */
let perAccessFilterPersistenceEnabled = false;

/** @returns {boolean} */
export function isPerAccessFilterPersistenceEnabled() {
  return perAccessFilterPersistenceEnabled;
}

/**
 * Called when LaunchDarkly idea-MDP-526 is resolved (or in tests).
 * @param {boolean} enabled
 */
export function setPerAccessFilterPersistenceEnabled(enabled) {
  perAccessFilterPersistenceEnabled = Boolean(enabled);
}

/** Exported for tests / debugging. */
export const PER_ACCESS_FILTER_FLAG_KEY = LD_FLAG_IDEA_MDP_526;

export function getAppData() {
  return JSON.parse(localStorage.getItem("exlAppData"));
}

export function setAppData(data) {
  localStorage.setItem("exlAppData", JSON.stringify(data));
}

export function updateEmployees(employees) {
  let exlAppData = getAppData();
  if (!exlAppData) {
    exlAppData = {};
  }
  exlAppData.employees = employees;
  setAppData(exlAppData);
}

export function getEmployees() {
  let exlAppData = getAppData();
  if (exlAppData && exlAppData.employees) {
    return Array.isArray(exlAppData.employees) ? exlAppData.employees : [];
  } else {
    return [];
  }
}

export function updatePositions(positions) {
  let exlAppData = getAppData();
  if (!exlAppData) {
    exlAppData = {};
  }
  exlAppData.positions = positions;
  setAppData(exlAppData);
}

export function getPositions() {
  let exlAppData = getAppData();
  if (exlAppData && exlAppData.positions) {
    return exlAppData.positions;
  } else {
    return [];
  }
}

export function updateRoles(roles) {
  let exlAppData = getAppData();
  if (!exlAppData) {
    exlAppData = {};
  }
  exlAppData.roles = roles;
  setAppData(exlAppData);
}

export function getRoles() {
  let exlAppData = getAppData();
  if (exlAppData && exlAppData.roles) {
    return exlAppData.roles;
  } else {
    return [];
  }
}

export function updateResponsibilities(responsibilities) {
  let exlAppData = getAppData();
  if (!exlAppData) {
    exlAppData = {};
  }
  exlAppData.responsibilities = responsibilities;
  setAppData(exlAppData);
}

export function getResponsibilities() {
  let exlAppData = getAppData();
  if (exlAppData && exlAppData.responsibilities) {
    return exlAppData.responsibilities;
  } else {
    return [];
  }
}

/**
 * Access storage id for filter/ordering persistence (ER-10189).
 *
 * Access = user-within-account. Prefer Access.id from `accessPermission`
 * (payload from getAppAccess). Fallback: `${userId}:${currentAccountID}`
 * when Access id is not present yet. Admin (no account Access) uses
 * `user:${userId}` so filter persistence still works there.
 *
 * @returns {string|null}
 */
export function getCurrentAccessStorageId() {
  try {
    const accessPermissionRaw = localStorage.getItem("accessPermission");
    if (accessPermissionRaw) {
      const accessPermission = JSON.parse(accessPermissionRaw);
      if (accessPermission?.id != null && accessPermission.id !== "") {
        return String(accessPermission.id);
      }
    }

    const userRaw = localStorage.getItem("user");
    const accountIdRaw = localStorage.getItem("currentAccountID");
    const user = userRaw ? JSON.parse(userRaw) : null;
    const accountId =
      accountIdRaw != null && accountIdRaw !== "" ? JSON.parse(accountIdRaw) : null;

    if (user?.id != null && accountId != null && accountId !== "") {
      return `${user.id}:${accountId}`;
    }

    // Admin portal (and similar) has a logged-in user but no account Access.
    if (user?.id != null) {
      return `user:${user.id}`;
    }
  } catch (e) {
    // ignore malformed localStorage
  }
  return null;
}

const FILTERS_MIGRATED_FLAG = "filtersMigratedToAccess";
const ORDERING_MIGRATED_FLAG = "orderingMigratedToAccess";

/** Saved filter payloads must be arrays; localStorage can be corrupted or legacy-shaped. */
function coerceStoredFiltersArray(value, fallback) {
  if (Array.isArray(value)) return value;
  return fallback;
}

/**
 * One-time: copy legacy unscoped `filters` / `filterMeta` into the current Access
 * bucket, then remove the legacy keys so they cannot leak to another Access.
 * First Access to touch storage after upgrade inherits prior browser values.
 */
function migrateLegacyFiltersIfNeeded(exlAppData, accessId) {
  if (exlAppData[FILTERS_MIGRATED_FLAG]) {
    return false;
  }

  exlAppData.filtersByAccess = exlAppData.filtersByAccess || {};
  exlAppData.filterMetaByAccess = exlAppData.filterMetaByAccess || {};

  const legacyFilters = exlAppData.filters;
  const legacyMeta = exlAppData.filterMeta;
  const hasLegacyFilters =
    legacyFilters && typeof legacyFilters === "object" && Object.keys(legacyFilters).length > 0;
  const hasLegacyMeta =
    legacyMeta && typeof legacyMeta === "object" && Object.keys(legacyMeta).length > 0;

  if (hasLegacyFilters) {
    const existing = exlAppData.filtersByAccess[accessId];
    const hasScoped = existing && Object.keys(existing).length > 0;
    if (!hasScoped) {
      exlAppData.filtersByAccess[accessId] = { ...legacyFilters };
    }
  }

  if (hasLegacyMeta) {
    const existingMeta = exlAppData.filterMetaByAccess[accessId];
    const hasScopedMeta = existingMeta && Object.keys(existingMeta).length > 0;
    if (!hasScopedMeta) {
      exlAppData.filterMetaByAccess[accessId] = { ...legacyMeta };
    }
  }

  delete exlAppData.filters;
  delete exlAppData.filterMeta;
  exlAppData[FILTERS_MIGRATED_FLAG] = true;
  return true;
}

function getOrCreateAccessFilters(exlAppData, accessId) {
  exlAppData.filtersByAccess = exlAppData.filtersByAccess || {};
  if (!exlAppData.filtersByAccess[accessId]) {
    exlAppData.filtersByAccess[accessId] = {};
  }
  return exlAppData.filtersByAccess[accessId];
}

function getOrCreateAccessFilterMeta(exlAppData, accessId) {
  exlAppData.filterMetaByAccess = exlAppData.filterMetaByAccess || {};
  if (!exlAppData.filterMetaByAccess[accessId]) {
    exlAppData.filterMetaByAccess[accessId] = {};
  }
  return exlAppData.filterMetaByAccess[accessId];
}

function readAccessFiltersMap(accessId) {
  let exlAppData = getAppData() || {};
  if (migrateLegacyFiltersIfNeeded(exlAppData, accessId)) {
    setAppData(exlAppData);
  }
  return (exlAppData.filtersByAccess && exlAppData.filtersByAccess[accessId]) || {};
}

function readAccessFilterMetaMap(accessId) {
  let exlAppData = getAppData() || {};
  if (migrateLegacyFiltersIfNeeded(exlAppData, accessId)) {
    setAppData(exlAppData);
  }
  return (exlAppData.filterMetaByAccess && exlAppData.filterMetaByAccess[accessId]) || {};
}

export function updateFilters(key, filters) {
  if (!isPerAccessFilterPersistenceEnabled()) {
    // Historical top-level localStorage keys (admin / scorecard / period tables)
    if (LEGACY_TOP_LEVEL_FILTER_KEYS.includes(key)) {
      try {
        localStorage.setItem(key, JSON.stringify(filters));
      } catch (e) {
        /* ignore */
      }
      return;
    }

    let exlAppData = getAppData() || {};
    exlAppData.filters = exlAppData.filters || {};
    exlAppData.filters[key] = filters;
    setAppData(exlAppData);
    return;
  }

  const accessId = getCurrentAccessStorageId();
  // Avoid writing unscoped values when Access context is not available yet.
  if (!accessId) {
    return;
  }

  let exlAppData = getAppData() || {};
  migrateLegacyFiltersIfNeeded(exlAppData, accessId);
  const accessFilters = getOrCreateAccessFilters(exlAppData, accessId);
  accessFilters[key] = filters;
  setAppData(exlAppData);
}

/**
 * Non-array filter metadata (e.g. department object + scope) keyed alongside the filter values.
 * Kept separate from `updateFilters` because that store coerces everything to an array.
 */
export function updateFilterMeta(key, meta) {
  if (!isPerAccessFilterPersistenceEnabled()) {
    let exlAppData = getAppData() || {};
    exlAppData.filterMeta = exlAppData.filterMeta || {};
    exlAppData.filterMeta[key] = meta;
    setAppData(exlAppData);
    return;
  }

  const accessId = getCurrentAccessStorageId();
  if (!accessId) {
    return;
  }

  let exlAppData = getAppData() || {};
  migrateLegacyFiltersIfNeeded(exlAppData, accessId);
  const accessMeta = getOrCreateAccessFilterMeta(exlAppData, accessId);
  accessMeta[key] = meta;
  setAppData(exlAppData);
}

export function getFilterMeta(key) {
  if (!isPerAccessFilterPersistenceEnabled()) {
    const exlAppData = getAppData();
    if (exlAppData && exlAppData.filterMeta && exlAppData.filterMeta[key]) {
      return exlAppData.filterMeta[key];
    }
    return null;
  }

  const accessId = getCurrentAccessStorageId();
  if (!accessId) {
    return null;
  }

  const metaMap = readAccessFilterMetaMap(accessId);
  return metaMap[key] || null;
}

function getLegacyDefaultFiltersForKey(key) {
  if (key === "redinessResponsibilityChecklist") {
    return ["active", "draft"];
  }
  if (key === "readinessResponsibilityAssignment") {
    return ["active"];
  }
  return null;
}

function consumeTopLevelLegacyFilters(key) {
  if (!LEGACY_TOP_LEVEL_FILTER_KEYS.includes(key)) {
    return undefined;
  }
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) {
      return undefined;
    }
    const parsed = JSON.parse(raw);
    localStorage.removeItem(key);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch (e) {
    return undefined;
  }
}

function getFiltersLegacy(key) {
  if (LEGACY_TOP_LEVEL_FILTER_KEYS.includes(key)) {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      /* ignore */
    }
    const registered = getDefaultFiltersForKey(key);
    if (registered) {
      return registered;
    }
    return [];
  }

  const exlAppData = getAppData();
  const defaults = getLegacyDefaultFiltersForKey(key);

  if (key === "redinessResponsibilityChecklist" && exlAppData && exlAppData.filters) {
    return typeof exlAppData.filters[key] === "undefined"
      ? ["active", "draft"]
      : coerceStoredFiltersArray(exlAppData.filters[key], ["active", "draft"]);
  }
  if (key === "readinessResponsibilityAssignment") {
    if (!exlAppData || !exlAppData.filters) {
      return ["active"];
    }
    return typeof exlAppData.filters[key] === "undefined"
      ? ["active"]
      : coerceStoredFiltersArray(exlAppData.filters[key], ["active"]);
  }
  if (exlAppData && exlAppData.filters && exlAppData.filters[key]) {
    return coerceStoredFiltersArray(exlAppData.filters[key], []);
  }
  return defaults ? [...defaults] : [];
}

export function getFilters(key) {
  if (!isPerAccessFilterPersistenceEnabled()) {
    return getFiltersLegacy(key);
  }

  const defaults = getDefaultFiltersForKey(key);
  const accessId = getCurrentAccessStorageId();
  if (!accessId) {
    return defaults ? [...defaults] : [];
  }

  const filtersMap = readAccessFiltersMap(accessId);

  if (typeof filtersMap[key] === "undefined") {
    const migratedTopLevel = consumeTopLevelLegacyFilters(key);
    if (migratedTopLevel) {
      // Persist under Access scope, then return.
      updateFilters(key, migratedTopLevel);
      return coerceStoredFiltersArray(migratedTopLevel, defaults ? [...defaults] : []);
    }
  }

  if (defaults) {
    return typeof filtersMap[key] === "undefined"
      ? [...defaults]
      : coerceStoredFiltersArray(filtersMap[key], [...defaults]);
  }

  if (filtersMap[key]) {
    return coerceStoredFiltersArray(filtersMap[key], []);
  }
  return [];
}

/**
 * One-time migrate legacy top-level `positions.ordering` (and similar) into
 * Access-scoped `orderingByAccess`, then remove the unscoped key.
 */
function migrateLegacyOrderingIfNeeded(exlAppData, accessId, key) {
  if (exlAppData[ORDERING_MIGRATED_FLAG]) {
    return false;
  }

  exlAppData.orderingByAccess = exlAppData.orderingByAccess || {};
  exlAppData.orderingByAccess[accessId] = exlAppData.orderingByAccess[accessId] || {};

  try {
    if (typeof localStorage !== "undefined") {
      const legacy = localStorage.getItem(key);
      if (legacy != null && exlAppData.orderingByAccess[accessId][key] == null) {
        exlAppData.orderingByAccess[accessId][key] = legacy;
      }
      localStorage.removeItem(key);
    }
  } catch (e) {
    // ignore storage errors
  }

  exlAppData[ORDERING_MIGRATED_FLAG] = true;
  return true;
}

/** Access-scoped persisted ordering (e.g. positions table sort). */
export function getPersistedOrdering(key) {
  if (!isPerAccessFilterPersistenceEnabled()) {
    try {
      if (typeof localStorage !== "undefined") {
        return localStorage.getItem(key);
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  const accessId = getCurrentAccessStorageId();
  if (!accessId) {
    return null;
  }

  let exlAppData = getAppData() || {};
  if (migrateLegacyOrderingIfNeeded(exlAppData, accessId, key)) {
    setAppData(exlAppData);
  }

  const byAccess = exlAppData.orderingByAccess && exlAppData.orderingByAccess[accessId];
  return byAccess && byAccess[key] != null ? byAccess[key] : null;
}

export function updatePersistedOrdering(key, value) {
  if (!isPerAccessFilterPersistenceEnabled()) {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(key, value);
      }
    } catch (e) {
      /* ignore */
    }
    return;
  }

  const accessId = getCurrentAccessStorageId();
  if (!accessId) {
    return;
  }

  let exlAppData = getAppData() || {};
  migrateLegacyOrderingIfNeeded(exlAppData, accessId, key);
  exlAppData.orderingByAccess = exlAppData.orderingByAccess || {};
  exlAppData.orderingByAccess[accessId] = exlAppData.orderingByAccess[accessId] || {};
  exlAppData.orderingByAccess[accessId][key] = value;
  setAppData(exlAppData);
}

/**
 * Updates the user's employee avatar in localStorage ONLY if the employee matches the logged-in user
 * @param {Object} employee - The employee object (must have id or first_name property)
 * @param {string} avatarUrl - The new avatar URL (or empty string if removed)
 */
export function updateUserAvatarInStorage(employee, avatarUrl) {
  try {
    const userStr = localStorage.getItem("user");
    if (!userStr) {
      return;
    }

    const user = JSON.parse(userStr);
    
    // Check if the employee being updated is the logged-in user
    // Match by employee ID or first name
    const isLoggedInUser = 
      (employee?.id && user?.employee?.id && employee.id === user.employee.id) ||
      (employee?.first_name && user?.first_name && 
       employee.first_name.toLowerCase() === user.first_name.toLowerCase());
    
    // Only update if this is the logged-in user
    if (!isLoggedInUser) {
      return;
    }
    
    // Ensure employee object exists
    if (!user.employee) {
      user.employee = {};
    }
    
    // Update the avatar
    user.employee.avatar = avatarUrl || "";
      
    // Save back to localStorage
    localStorage.setItem("user", JSON.stringify(user));
    
    // Emit custom event to notify components (like SideBar) to refresh
    const event = new CustomEvent("userAvatarUpdated", { 
      detail: { employeeId: employee?.id, avatarUrl },
      bubbles: true,
      cancelable: true
    });
    
    window.dispatchEvent(event);
    document.dispatchEvent(event);
    
    // Also try to trigger via Vue's event system if available
    if (typeof window !== 'undefined' && window.Vue) {
      if (window.Vue.prototype && window.Vue.prototype.$root) {
        window.Vue.prototype.$root.$emit("userAvatarUpdated", { employeeId: employee?.id, avatarUrl });
      }
    }
    
  } catch (error) {
    console.error("Error updating user avatar in localStorage:", error);
  }
}

